import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './styles/ErpDashboard.module.css';
import {
    FaArrowUp, FaArrowDown, FaMinus, FaExclamationTriangle,
    FaCashRegister, FaFileInvoiceDollar, FaBoxOpen, FaUsers, FaTags
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ---------------------------------------------------------------------------
 * Tipos de las respuestas del backend que consume el dashboard.
 * Se mapean 1:1 con lo que devuelven los endpoints; los campos opcionales son
 * los que el backend puede omitir segun el estado de los datos.
 * ------------------------------------------------------------------------- */

interface PeriodMetrics {
    total: number;
    pedidos: number;
    ticket_promedio: number;
    dias: number[];
}

interface LocalWeekly {
    id_local: number;
    nombre_local: string;
    current: PeriodMetrics;
    previous: PeriodMetrics;
    delta_total_pct: number | null;
    delta_pedidos_pct: number | null;
}

interface WeeklyResponse {
    current_week: { start: string; end: string };
    previous_week: { start: string; end: string };
    day_labels: string[];
    totals: {
        current: PeriodMetrics;
        previous: PeriodMetrics;
        delta_total_pct: number | null;
        delta_pedidos_pct: number | null;
    };
    locales: LocalWeekly[];
}

interface ProductStat {
    Producto?: string;
    categoria?: string;
    semana: number;
    total: number;
    cantidad?: number;
}

interface MethodStat {
    metodo?: string;
    semana: number;
    total: number;
}

interface FaltanteItem {
    idProductos: number;
    NombresProductos: string;
    subcategoria?: string | null;
    stock_actual?: number;
    stock_minimo?: number;
    faltante?: number;
    disponible?: number;
}

interface Local {
    locales_id: number;
    nombre_local: string;
}

const money = (value: number) =>
    new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        maximumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0);

const compactMoney = (value: number) => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(value / 1_000)}k`;
    return `$${Math.round(value)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const monthStartISO = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

/**
 * Tarjeta de KPI con delta respecto al periodo anterior.
 * `delta` en null significa "sin base de comparacion" (semana previa en cero),
 * caso en el que no se muestra porcentaje para no inventar un +infinito.
 */
const KpiCard: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string;
    delta?: number | null;
    hint?: string;
}> = ({ icon, label, value, delta, hint }) => {
    const trend = delta == null ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
    const TrendIcon = trend === 'up' ? FaArrowUp : trend === 'down' ? FaArrowDown : FaMinus;

    return (
        <article className={styles.kpiCard}>
            <div className={styles.kpiHead}>
                <span className={styles.kpiIcon}>{icon}</span>
                <span className={styles.kpiLabel}>{label}</span>
            </div>
            <strong className={styles.kpiValue}>{value}</strong>
            <div className={styles.kpiFoot}>
                {delta != null && (
                    <span className={`${styles.kpiDelta} ${styles[trend]}`}>
                        <TrendIcon /> {Math.abs(delta).toFixed(1)}%
                    </span>
                )}
                {hint && <span className={styles.kpiHint}>{hint}</span>}
            </div>
        </article>
    );
};

/** Barras verticales de ventas por dia. Escala relativa al maximo de la serie. */
const DayBars: React.FC<{ labels: string[]; current: number[]; previous: number[] }> = ({
    labels, current, previous,
}) => {
    const max = Math.max(...current, ...previous, 1);
    return (
        <div className={styles.barChart}>
            {labels.map((label, i) => (
                <div key={label} className={styles.barCol}>
                    <div className={styles.barTrack}>
                        <div
                            className={styles.barPrev}
                            style={{ height: `${(previous[i] / max) * 100}%` }}
                            title={`Semana pasada: ${money(previous[i])}`}
                        />
                        <div
                            className={styles.barCurr}
                            style={{ height: `${(current[i] / max) * 100}%` }}
                            title={`Esta semana: ${money(current[i])}`}
                        />
                    </div>
                    <span className={styles.barValue}>{compactMoney(current[i])}</span>
                    <span className={styles.barLabel}>{label.slice(0, 3)}</span>
                </div>
            ))}
        </div>
    );
};

const ErpDashboard: React.FC = () => {
    const { defaultLocalId, user } = useAuth();

    const [locales, setLocales] = useState<Local[]>([]);
    const [selectedLocal, setSelectedLocal] = useState<string>('');
    const [weekly, setWeekly] = useState<WeeklyResponse | null>(null);
    const [products, setProducts] = useState<ProductStat[]>([]);
    const [methods, setMethods] = useState<MethodStat[]>([]);
    const [faltantes, setFaltantes] = useState<FaltanteItem[]>([]);
    const [clientCount, setClientCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // El local activo de la sesion es el valor por defecto del filtro.
    useEffect(() => {
        if (defaultLocalId != null) setSelectedLocal(String(defaultLocalId));
    }, [defaultLocalId]);

    useEffect(() => {
        fetch(`${API_URL}/locales`)
            .then(r => r.json())
            .then(data => Array.isArray(data) && setLocales(data))
            .catch(() => { /* el selector queda vacio: el dashboard funciona sin filtro */ });
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);

        const localQS = selectedLocal ? `&id_local=${selectedLocal}` : '';
        const start = monthStartISO();
        const end = todayISO();

        // Cada bloque del dashboard es independiente: un endpoint caido degrada
        // solo su tarjeta en vez de tumbar la pantalla entera.
        const [weeklyRes, prodRes, methRes, faltRes, cliRes] = await Promise.allSettled([
            fetch(`${API_URL}/admin/stats/local-weekly-comparison`).then(r => r.json()),
            fetch(`${API_URL}/admin/stats/weekly-products?start_date=${start}&end_date=${end}${localQS}`).then(r => r.json()),
            fetch(`${API_URL}/admin/stats/sales-by-method?type=payment&start_date=${start}&end_date=${end}${localQS}`).then(r => r.json()),
            fetch(`${API_URL}/admin/stock/faltante${selectedLocal ? `?id_local=${selectedLocal}` : ''}`).then(r => r.json()),
            fetch(`${API_URL}/admin/miembros`).then(r => r.json()),
        ]);

        if (weeklyRes.status === 'fulfilled' && weeklyRes.value?.totals) {
            setWeekly(weeklyRes.value);
        } else {
            setError('No se pudieron cargar las ventas de la semana.');
        }
        if (prodRes.status === 'fulfilled' && Array.isArray(prodRes.value)) setProducts(prodRes.value);
        if (methRes.status === 'fulfilled' && Array.isArray(methRes.value)) setMethods(methRes.value);
        if (faltRes.status === 'fulfilled' && Array.isArray(faltRes.value)) setFaltantes(faltRes.value);
        if (cliRes.status === 'fulfilled' && Array.isArray(cliRes.value)) setClientCount(cliRes.value.length);

        setLoading(false);
    }, [selectedLocal]);

    useEffect(() => { loadAll(); }, [loadAll]);

    /**
     * Cuando hay un local seleccionado se muestran sus metricas; sin filtro, los
     * totales de la cadena. Se normaliza a una forma unica {current, previous,
     * deltas} para que los KPIs no tengan que distinguir el origen.
     */
    const scope = useMemo(() => {
        if (!weekly) return null;
        if (selectedLocal) {
            const found = weekly.locales.find(l => String(l.id_local) === selectedLocal);
            if (found) {
                return {
                    current: found.current,
                    previous: found.previous,
                    delta_total_pct: found.delta_total_pct,
                    delta_pedidos_pct: found.delta_pedidos_pct,
                };
            }
        }
        return weekly.totals;
    }, [weekly, selectedLocal]);

    // weekly-products devuelve una fila por producto y semana: se agrega por producto.
    const topProducts = useMemo(() => {
        const acc = new Map<string, { nombre: string; total: number; cantidad: number }>();
        products.forEach(p => {
            const nombre = p.Producto || 'Sin nombre';
            const prev = acc.get(nombre) || { nombre, total: 0, cantidad: 0 };
            prev.total += Number(p.total) || 0;
            prev.cantidad += Number(p.cantidad) || 0;
            acc.set(nombre, prev);
        });
        return Array.from(acc.values()).sort((a, b) => b.total - a.total).slice(0, 6);
    }, [products]);

    const byMethod = useMemo(() => {
        const acc = new Map<string, number>();
        methods.forEach(m => {
            const key = m.metodo || 'Sin especificar';
            acc.set(key, (acc.get(key) || 0) + (Number(m.total) || 0));
        });
        const rows = Array.from(acc.entries()).map(([metodo, total]) => ({ metodo, total }));
        const sum = rows.reduce((s, r) => s + r.total, 0) || 1;
        return rows
            .sort((a, b) => b.total - a.total)
            .map(r => ({ ...r, pct: (r.total / sum) * 100 }));
    }, [methods]);

    const localName = selectedLocal
        ? locales.find(l => String(l.locales_id) === selectedLocal)?.nombre_local ?? 'Local'
        : 'Todos los locales';

    return (
        <div className={styles.dashboard}>
            <header className={styles.header}>
                <div>
                    <span className={styles.eyebrow}>Panel general</span>
                    <h2 className={styles.title}>Hola, {user || 'Admin'}</h2>
                    <p className={styles.subtitle}>
                        {weekly
                            ? `Semana del ${weekly.current_week.start} al ${weekly.current_week.end} · ${localName}`
                            : localName}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <select
                        className={styles.localSelect}
                        value={selectedLocal}
                        onChange={e => setSelectedLocal(e.target.value)}
                    >
                        <option value="">Todos los locales</option>
                        {locales.map(l => (
                            <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                        ))}
                    </select>
                    <button className={styles.refreshBtn} onClick={loadAll} disabled={loading}>
                        {loading ? 'Cargando…' : 'Actualizar'}
                    </button>
                </div>
            </header>

            {error && <div className={styles.errorBar}>{error}</div>}

            <section className={styles.kpiGrid}>
                <KpiCard
                    icon={<FaCashRegister />}
                    label="Ventas de la semana"
                    value={money(scope?.current.total ?? 0)}
                    delta={scope?.delta_total_pct}
                    hint="vs. semana anterior"
                />
                <KpiCard
                    icon={<FaFileInvoiceDollar />}
                    label="Comprobantes emitidos"
                    value={String(scope?.current.pedidos ?? 0)}
                    delta={scope?.delta_pedidos_pct}
                    hint="vs. semana anterior"
                />
                <KpiCard
                    icon={<FaTags />}
                    label="Ticket promedio"
                    value={money(scope?.current.ticket_promedio ?? 0)}
                    hint="Semana en curso"
                />
                <KpiCard
                    icon={<FaUsers />}
                    label="Clientes registrados"
                    value={clientCount != null ? String(clientCount) : '—'}
                    hint="Base total"
                />
            </section>

            <section className={styles.grid}>
                <article className={`${styles.panel} ${styles.panelWide}`}>
                    <header className={styles.panelHead}>
                        <h3>Ventas por día</h3>
                        <div className={styles.legend}>
                            <span><i className={styles.dotCurr} /> Esta semana</span>
                            <span><i className={styles.dotPrev} /> Anterior</span>
                        </div>
                    </header>
                    {weekly && scope ? (
                        <DayBars
                            labels={weekly.day_labels}
                            current={scope.current.dias}
                            previous={scope.previous.dias}
                        />
                    ) : (
                        <p className={styles.empty}>Sin datos de ventas para el período.</p>
                    )}
                </article>

                <article className={styles.panel}>
                    <header className={styles.panelHead}>
                        <h3>Medios de pago</h3>
                        <span className={styles.panelMeta}>Mes en curso</span>
                    </header>
                    {byMethod.length ? (
                        <ul className={styles.methodList}>
                            {byMethod.map(m => (
                                <li key={m.metodo} className={styles.methodRow}>
                                    <div className={styles.methodTop}>
                                        <span className={styles.methodName}>{m.metodo}</span>
                                        <span className={styles.methodValue}>{money(m.total)}</span>
                                    </div>
                                    <div className={styles.methodTrack}>
                                        <div className={styles.methodFill} style={{ width: `${m.pct}%` }} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={styles.empty}>Sin ventas registradas este mes.</p>
                    )}
                </article>

                <article className={styles.panel}>
                    <header className={styles.panelHead}>
                        <h3>Artículos más vendidos</h3>
                        <span className={styles.panelMeta}>Mes en curso</span>
                    </header>
                    {topProducts.length ? (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Artículo</th>
                                    <th className={styles.num}>Unid.</th>
                                    <th className={styles.num}>Importe</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topProducts.map(p => (
                                    <tr key={p.nombre}>
                                        <td className={styles.ellipsis} title={p.nombre}>{p.nombre}</td>
                                        <td className={styles.num}>{p.cantidad}</td>
                                        <td className={styles.num}>{money(p.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className={styles.empty}>Sin ventas de artículos este mes.</p>
                    )}
                </article>

                <article className={`${styles.panel} ${styles.panelWide}`}>
                    <header className={styles.panelHead}>
                        <h3>
                            <FaExclamationTriangle className={styles.warnIcon} /> Reposición pendiente
                        </h3>
                        <span className={styles.panelMeta}>
                            {faltantes.length} artículo{faltantes.length === 1 ? '' : 's'} bajo mínimo
                        </span>
                    </header>
                    {faltantes.length ? (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Artículo</th>
                                    <th>Rubro</th>
                                    <th className={styles.num}>Disponible</th>
                                    <th className={styles.num}>Mínimo</th>
                                    <th className={styles.num}>Faltante</th>
                                </tr>
                            </thead>
                            <tbody>
                                {faltantes.slice(0, 8).map(f => (
                                    <tr key={f.idProductos}>
                                        <td className={styles.ellipsis} title={f.NombresProductos}>
                                            {f.NombresProductos}
                                        </td>
                                        <td className={styles.muted}>{f.subcategoria || '—'}</td>
                                        <td className={styles.num}>{f.disponible ?? f.stock_actual ?? 0}</td>
                                        <td className={styles.num}>{f.stock_minimo ?? 0}</td>
                                        <td className={`${styles.num} ${styles.danger}`}>{f.faltante ?? 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className={styles.empty}>
                            <FaBoxOpen /> Todo el stock está por encima del mínimo.
                        </p>
                    )}
                </article>
            </section>

            {weekly && weekly.locales.length > 1 && (
                <section className={styles.panel}>
                    <header className={styles.panelHead}>
                        <h3>Comparativa por local</h3>
                        <span className={styles.panelMeta}>Semana actual vs. anterior</span>
                    </header>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Local</th>
                                <th className={styles.num}>Ventas</th>
                                <th className={styles.num}>Comprobantes</th>
                                <th className={styles.num}>Ticket prom.</th>
                                <th className={styles.num}>Variación</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weekly.locales.map(l => (
                                <tr key={l.id_local}>
                                    <td>{l.nombre_local}</td>
                                    <td className={styles.num}>{money(l.current.total)}</td>
                                    <td className={styles.num}>{l.current.pedidos}</td>
                                    <td className={styles.num}>{money(l.current.ticket_promedio)}</td>
                                    <td className={styles.num}>
                                        {l.delta_total_pct == null ? (
                                            <span className={styles.muted}>—</span>
                                        ) : (
                                            <span
                                                className={`${styles.kpiDelta} ${
                                                    l.delta_total_pct > 0 ? styles.up
                                                        : l.delta_total_pct < 0 ? styles.down : styles.flat
                                                }`}
                                            >
                                                {l.delta_total_pct > 0 ? <FaArrowUp /> : l.delta_total_pct < 0 ? <FaArrowDown /> : <FaMinus />}
                                                {Math.abs(l.delta_total_pct).toFixed(1)}%
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}
        </div>
    );
};

export default ErpDashboard;
